import { Component, HostListener } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ConsultationsService } from '../../../../core/services/consultation/consultations.service';
import { CurrentUserService } from '../../../../core/services/user/current-user.service';
import { FormsModule } from '@angular/forms';
import { StatusUtils } from '../../../../utils/status-utils';
import { SignalRService } from '../../../../core/services/signalR/signalR';
import { environment } from '../../../../../environments/environment';
import { PatientRecordModel } from '../../../../core/models/consultation/consultation-list.model';
import { UserSessionModel } from '../../../../core/models/user/current-user.model';
import { CaseStatus } from '../../../../../enums/case-status.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BsDatepickerModule, BsLocaleService } from 'ngx-bootstrap/datepicker';
import { provideAnimations } from '@angular/platform-browser/animations';
import { DateFormatterUtil } from '../../../../utils/date-format-utils';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { nlLocale } from 'ngx-bootstrap/locale';
import { ChatStatus } from '../../../../../enums/chat-status.enum';
import { FilterModel } from '../../../../core/models/filters/filters.model';
import { TablesComponent } from '../../../components/tables/tables.component';
import { ColumnModel } from '../../../../core/models/table/column.model';
import { SortModel } from '../../../../core/models/filters/sort.model';
import { ToastService } from '../../../../core/services/toast.service';
import { UserRole } from '../../../../../enums/roles.enum';
defineLocale('nl', nlLocale);

export interface AIReportStatusModel {
  code: string;
  title: string;
}

@Component({
  selector: 'app-consultation-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    TranslateModule,
    BsDatepickerModule,
    TablesComponent,
  ],
  templateUrl: './consultation-list.component.html',
  styleUrl: './consultation-list.component.scss',
  providers: [DatePipe, provideAnimations()],
})
export class ConsultationListComponent {
  ChatStatus = ChatStatus;
  currentUser: UserSessionModel | null = null;
  consultationList: PatientRecordModel[] = [];
  consultationCount: number = 0;
  totalPage: number = 0;
  pageSize: number = 10;
  pageSizes = [10, 25, 50, 100];
  pageHeight: number;
  currentPage: number = 1;
  totalConsultations: number = 0;
  organizationList: any = [];
  caseStatuses = StatusUtils?.getFilterCaseStatuses();
  aiReportStatuses: AIReportStatusModel[];
  consultationListLoading: boolean = true;
  maxDate = new Date();
  caseStatus = CaseStatus;
  url: string = `${environment.apiLocations.patient}/PatientRecord`;
  signalrModel: any = null;
  filters: FilterModel[] = [
    //AGB Code
    {
      type: 'input',
      label: 'dermNo',
      placeholder: 'filterByDermNo',
      filterId: 'PIDNo',
      value: null,
    },
    //StartDate
    {
      type: 'date',
      label: 'startCreatedDate',
      placeholder: 'dd-mm-yyyy',
      filterId: 'StartCreatedDateTime',
      value: null,
    },
    //EndCreateDate
    {
      type: 'date',
      label: 'endCreatedDate',
      placeholder: 'dd-mm-yyyy',
      filterId: 'EndCreatedDateTime',
      value: null,
    },
    //PatientName
    {
      type: 'input',
      label: 'patientName',
      placeholder: 'filterByNameAndSurname',
      filterId: 'NameSurname',
      value: null,
    },
    //Status
    {
      type: 'select',
      label: 'status',
      placeholder: 'selectStatus',
      filterId: 'Status',
      value: null,
    },
  ];
  sort: SortModel | null = null;

  tableHeader: string = '';
  footerText: string = '';
  columns: ColumnModel[] = [];
  columnWidths: string[] = [
    'w-80',
    'w-125',
    'w-200',
    'w-150',
    'w-125',
    'w-225',
    'w-80',
  ];
  constructor(
    private consultationsService: ConsultationsService,
    private currentUserService: CurrentUserService,
    private signalRService: SignalRService,
    private translate: TranslateService,
    private bsLocaleService: BsLocaleService,
    private toastService: ToastService
  ) {
    this.setAIReportStatuses();
    const userLang = localStorage.getItem('selectedLanguage') || 'en';
    this.translate.use(userLang);
    this.createConclusionListConnection();
    this.createConclusionStatusConnection();
    this.bsLocaleService.use(userLang);
    this.columns = [
      { key: 'pidNo', translateKey: 'dermNo' },
      { key: 'createdDate', translateKey: 'createdDate' },
      { key: 'patientName', translateKey: 'patientName' },
      { key: 'customerCode', translateKey: 'customerCode' },
      { key: 'createdBy', translateKey: 'createdBy' },
      { key: 'patientStatus', translateKey: 'status' },
      { key: 'AI', translateKey: 'AI' },
    ];
    setTimeout(() => {
      this.tableHeader = this.translate.instant('consultationList');
      this.footerText = this.translate.instant('footerText.consultation');
    }, 100);
 
  }

  async ngOnInit() {
    await StatusUtils?.initializeTranslateService(this.translate);
    this.caseStatuses = StatusUtils?.getFilterCaseStatuses();
    this.currentUser = this.currentUserService.GetSession();
    if (
      this.currentUser?.role === UserRole.Admin ||
      this.currentUser?.role === UserRole.OrganizationAdmin ||
      this.currentUser?.role === UserRole.GeneralPractitioner
    ) {
      this.columns.push({
        key: 'actions',
        translateKey: 'actions',
        actionTypes: ['delete'],
      });
    }
    this.calculatePageSize();
    this.getConsultationList(this.currentPage, this.pageSize);

  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    const newHeight = window.innerHeight;

    if (newHeight !== this.pageHeight) {
      this.calculatePageSize();
    }
  }

  private setAIReportStatuses() {
    const manipulatedStatuses = [
      { code: 'true', title: this.translate.instant('yes') },
      { code: 'false', title: this.translate.instant('no') },
    ];
    this.aiReportStatuses = manipulatedStatuses;
  }

  private calculatePageSize(): void {
      const pageHeight = window.innerHeight;
      this.pageHeight = pageHeight;

      if (pageHeight < 600) {
        this.pageSize = 5;
      } else if (pageHeight < 1000) {
        this.pageSize = 10;
      } else if (pageHeight < 1400) {
        this.pageSize = 25;
      } else {
        this.pageSize = 50;
      }
      if (this.filters.some((filter) => !!filter.value)) {
        const dateFilters = this.filters.filter(
          (filter) =>
            (filter.filterId === 'StartCreatedDateTime' ||
              filter.filterId === 'EndCreatedDateTime') &&
            filter.value
        );

        dateFilters.forEach((filter) => {
          filter.value = DateFormatterUtil.formatDate(filter.value);
        });

        this.getConsultationList(
          1,
          this.pageSize,
          this.filters,
          this.sort ? this.sort : null
        );
      } else {
        this.getConsultationList(
          this.currentPage,
          this.pageSize,
          null,
          this.sort ? this.sort : null
        );
      }

  }

  protected async createConclusionListConnection() {
    const signalRBasePath = `${environment.apiLocations.patient}`.split(
      '/api/v1'
    )[0];
    const hubUrl = 'patient-hub';
    this.signalRService.connect(hubUrl, signalRBasePath);
    this.signalRService.on(hubUrl, 'receivePatientRecordCreated', signalRBasePath, (message) => {
      const parsedConsultation = JSON.parse(message);
      if (parsedConsultation) {
        const consultation: PatientRecordModel = {
          patientName: parsedConsultation.PatientName,
          patientLastName: parsedConsultation.PatientLastName,
          patientRecordId: parsedConsultation.PatientRecordId,
          customerCode: parsedConsultation.CustomerCode,
          createdBy: parsedConsultation.CreatedBy,
          patientStatus: parsedConsultation.PatientStatus,
          pidNo: parsedConsultation.PIDNo,
          createdDate: parsedConsultation.CreatedDate,
          chatStatuses: [],
        };
        this.consultationCount++;
        this.consultationList = [consultation, ...this.consultationList];
      }
    });
  }

  protected async createConclusionStatusConnection() {
    const signalRBasePath = `${environment.apiLocations.patient}`.split(
      '/api/v1'
    )[0];
    const hubUrl = 'patient-hub';
    this.signalRService.connect(hubUrl, signalRBasePath);
    this.signalRService.on(hubUrl, 'receivePatientUpdated', signalRBasePath, (message) => {
      const parsedJson = JSON.parse(message);
      this.signalrModel = parsedJson;
      
    });
  }

  filterConsultationList(filters?: FilterModel[], sort?: SortModel) {
    if (filters && sort === null) {
      const dateFilters = filters.filter(
        (filter) =>
          (filter.filterId === 'StartCreatedDateTime' ||
            filter.filterId === 'EndCreatedDateTime') &&
          filter.value
      );

      dateFilters.forEach((filter) => {
        filter.value = DateFormatterUtil.formatDate(filter.value);
      });

      this.getConsultationList(1, this.pageSize, filters);
    } else if (sort && filters.every((filter) => filter.value === null)) {
      this.sort = sort;
      sort.column = this.getSortByValue(sort.column);
      this.getConsultationList(this.currentPage, this.pageSize, null, sort);
    } else {
      this.getConsultationList(this.currentPage, this.pageSize, filters, sort);
    }
  }

  getConsultationList(
    page: number,
    size: number,
    filters?: FilterModel[],
    sort?: SortModel
  ): void {
    this.consultationListLoading = true;
    this.consultationsService
      .consultationList(page, size, filters, sort)
      .subscribe({
        next: (res) => {
          this.consultationList = res.items;
          this.consultationCount = res.count;
          this.totalPage = res.pages;
          this.pageSize = res.size;
          this.currentPage = page;
          this.consultationListLoading = false;
        },
      });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.getConsultationList(
      this.currentPage,
      this.pageSize,
      this.filters,
      this.sort ? this.sort : null
    );
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.getConsultationList(
      this.currentPage,
      this.pageSize,
      this.filters,
      this.sort ? this.sort : null
    );
  }

  getSortByValue(column: string): string {
    switch (column) {
      case 'pidNo':
        return 'Patient.PidNo';
      case 'patientName':
        return 'Patient.Name';
      case 'patientStatus':
        return 'PatientRecordCases.PatientStatus';
      case 'createdBy':
        return 'CreatedUser';
      default:
        return column;
    }
  }

  actionButtonClick(actionType: string, row: any): void {
    switch (actionType) {
      case 'delete':
        this.deleteConsultation(row.patientRecordId);
        break;
    }
  }

  deleteConsultation(id: string): void {
    this.consultationsService.deleteConsultation(id).subscribe({
      next: () => {
        this.toastService.showToast(
          this.translate.instant('successDeleteConsultationDesc'),
          'success'
        );
        this.getConsultationList(1, this.pageSize, this.filters);
      },
    });
  }
}
